import React from 'react';
import { AlertCircle, Lock } from 'lucide-react';

export default function ProposedPricingBlock({
  proposedPricing,
  setProposedPricing,
  merchantFeesAutoCalculated,
  setMerchantFeesAutoCalculated,
  achFeeType = 'flat', // default to 'flat' for backward compatibility
  userRole = 'preparer',
  originalMerchantFees = { ACH: 0, CC: 0, AMEX: 0, DEBIT: 0 },
  readerCostRecoveryApplied = false,
  originalRateBeforeRecovery = null
}) {
  // Check if user can edit merchant fees
  const canEditMerchantFees = !['preparer', 'account_executive'].includes(userRole);
  
  // Check if a specific merchant fee has been customized
  const isFeeCustomized = (paymentType) => {
    const currentFee = proposedPricing.paymentRates[paymentType]?.merchantFee || 0;
    const originalFee = originalMerchantFees[paymentType] || 0;
    return Math.abs(currentFee - originalFee) > 0.01; // Allow for floating point precision
  };
  
  // Check if a custom fee matches the service manager's rate
  const doesCustomFeeMatchService = (paymentType) => {
    const currentFee = proposedPricing.paymentRates[paymentType]?.merchantFee || 0;
    const originalFee = originalMerchantFees[paymentType] || 0;
    return Math.abs(currentFee - originalFee) <= 0.01; // Within floating point precision
  };
  
  return (
    <div className="card-modern p-0 overflow-hidden">
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 relative">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        <h2 className="text-lg font-bold text-white relative z-10">
          BLOCK 4: PROPOSED PRICING — TIERED STRUCTURE <span className="text-accent text-sm ml-2">(All fields required *)</span>
        </h2>
      </div>
      <div className="p-6">
      
      {/* Reader Cost Recovery Applied - Rates Locked */}
      {readerCostRecoveryApplied && originalRateBeforeRecovery && (
        <div className="mb-5 p-4 bg-green-50 border-2 border-green-400 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 rounded-xl">
              <Lock className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-green-900 font-bold mb-2">✅ Reader Cost Recovery Applied - Rates Locked</p>
              <p className="text-xs text-green-800 mb-3">
                Take rates have been automatically adjusted to cover reader hardware costs and are now <strong>locked</strong>. You cannot modify these rates once recovery has been applied.
              </p>
              <div className="bg-white border border-green-300 rounded-lg p-3">
                <p className="text-xs font-bold text-green-900 mb-2">Rate Changes Applied:</p>
                <div className="space-y-1 text-xs">
                  {originalRateBeforeRecovery.CC !== undefined && (
                    <div className="flex justify-between items-center py-1 border-b border-green-200">
                      <span className="text-slate-600">CC Take Rate:</span>
                      <span>
                        <span className="text-red-600 line-through mr-2">{originalRateBeforeRecovery.CC?.toFixed(2)}%</span>
                        →
                        <span className="text-green-700 font-bold ml-2">{proposedPricing.paymentRates.CC?.takeRate?.toFixed(2)}%</span>
                        <span className="text-green-600 ml-2">(+{originalRateBeforeRecovery.rateIncrease?.toFixed(2)}%)</span>
                      </span>
                    </div>
                  )}
                  {originalRateBeforeRecovery.AMEX !== undefined && (
                    <div className="flex justify-between items-center py-1 border-b border-green-200">
                      <span className="text-slate-600">AMEX Take Rate:</span>
                      <span>
                        <span className="text-red-600 line-through mr-2">{originalRateBeforeRecovery.AMEX?.toFixed(2)}%</span>
                        →
                        <span className="text-green-700 font-bold ml-2">{proposedPricing.paymentRates.AMEX?.takeRate?.toFixed(2)}%</span>
                        <span className="text-green-600 ml-2">(+{originalRateBeforeRecovery.rateIncrease?.toFixed(2)}%)</span>
                      </span>
                    </div>
                  )}
                  {originalRateBeforeRecovery.DEBIT !== undefined && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-slate-600">DEBIT Take Rate:</span>
                      <span>
                        <span className="text-red-600 line-through mr-2">{originalRateBeforeRecovery.DEBIT?.toFixed(2)}%</span>
                        →
                        <span className="text-green-700 font-bold ml-2">{proposedPricing.paymentRates.DEBIT?.takeRate?.toFixed(2)}%</span>
                        <span className="text-green-600 ml-2">(+{originalRateBeforeRecovery.rateIncrease?.toFixed(2)}%)</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Merchant Fee Restriction Notice — only shown to roles that CAN see but not edit the column */}
      {['cfo', 'ceo', 'admin'].includes(userRole) && !canEditMerchantFees && (
        <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-xl">
            <Lock className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-blue-900 font-semibold">Merchant Fee Editing Restricted</p>
            <p className="text-xs text-blue-700 mt-1">
              Your role ({userRole}) does not have permission to modify merchant fees. These values are automatically calculated based on the selected service and processor.
            </p>
          </div>
        </div>
      )}
      
      {/* Tier Thresholds */}
      <div className="mb-6">
        <h3 className="font-semibold mb-3">Tier Thresholds</h3>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3].map(tier => (
            <React.Fragment key={tier}>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Tier {tier} Max Amount <span className="text-blue-600 font-bold">($)</span> <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-xs">$</span>
                  <input
                    type="number"
                    value={proposedPricing[`tier${tier}MaxAmount`]}
                    onChange={(e) => setProposedPricing({ ...proposedPricing, [`tier${tier}MaxAmount`]: parseFloat(e.target.value) || 0 })}
                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                    className="input-field text-sm bg-yellow-50 pl-6"
                    step="0.01"
                    placeholder={tier === 1 ? "50" : tier === 2 ? "100" : "9999"}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Tier {tier} Minimum Fee <span className="text-blue-600 font-bold">($)</span> <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-xs">$</span>
                  <input
                    type="number"
                    value={proposedPricing[`tier${tier}MinFee`]}
                    onChange={(e) => setProposedPricing({ ...proposedPricing, [`tier${tier}MinFee`]: parseFloat(e.target.value) || 0 })}
                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                    className="input-field text-sm bg-yellow-50 pl-6"
                    step="0.01"
                    placeholder={tier === 1 ? "1.00" : tier === 2 ? "1.50" : "2.00"}
                  />
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Payment-Type Specific Rates */}
      <div>
        <h3 className="font-semibold mb-3">Payment-Type Specific Rates <span className="text-red-600 text-xs">(FPA INPUT)</span></h3>
        <div className="overflow-x-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th className="text-left p-2 whitespace-nowrap">Payment Type</th>
                <th className="text-left p-2 whitespace-nowrap">Take Rate <span className="text-blue-600 font-bold">(%)</span> <span className="text-red-600">*</span></th>
                <th className="text-left p-2 whitespace-nowrap">Minimum Fee <span className="text-blue-600 font-bold">($)</span></th>
                {/* Only show Merchant Fee column to CFO/CEO/Admin */}
                {['cfo', 'ceo', 'admin'].includes(userRole) && (
                  <th className="text-left p-2 whitespace-nowrap">
                    Merchant Fee <span className="text-blue-600 font-bold">({achFeeType === 'flat' ? '$ or %' : '%'})</span>
                    <span className="text-xs text-slate-500 ml-1" title="ACH: Flat fee per transaction. Cards: Percentage of transaction amount">ⓘ</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {['ACH', 'CC', 'AMEX', 'DEBIT'].map(type => (
                <tr key={type} className="border-b">
                  <td className="p-2 font-medium">{type}</td>
                  <td className="p-2">
                    {type === 'ACH' ? (
                      <div className="px-3 py-2 bg-slate-100 text-slate-500 rounded text-sm text-center">
                        N/A
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="number"
                          value={proposedPricing.paymentRates[type].takeRate}
                          onChange={(e) => setProposedPricing({
                            ...proposedPricing,
                            paymentRates: {
                              ...proposedPricing.paymentRates,
                              [type]: { ...proposedPricing.paymentRates[type], takeRate: parseFloat(e.target.value) || 0 }
                            }
                          })}
                          onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                          disabled={readerCostRecoveryApplied}
                          className={`input-field text-sm w-24 pr-7 ${readerCostRecoveryApplied ? 'bg-green-100 cursor-not-allowed' : 'bg-yellow-50'}`}
                          step="0.01"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-xs">%</span>
                        {readerCostRecoveryApplied && (
                          <Lock className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-green-600" />
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-xs">$</span>
                      <input
                        type="number"
                        value={proposedPricing.paymentRates[type].minFee}
                        onChange={(e) => setProposedPricing({
                          ...proposedPricing,
                          paymentRates: {
                            ...proposedPricing.paymentRates,
                            [type]: { ...proposedPricing.paymentRates[type], minFee: parseFloat(e.target.value) || 0 }
                          }
                        })}
                        onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                        className="input-field text-sm w-24 bg-yellow-50 pl-6"
                        step="0.01"
                      />
                    </div>
                  </td>
                  {/* Merchant Fee column: only shown to CFO/CEO/Admin */}
                  {['cfo', 'ceo', 'admin'].includes(userRole) && (
                  <td className="p-2">
                    <div className="flex items-center space-x-2">
                      <div className="relative flex-1">
                        <input
                          type="number"
                          value={proposedPricing.paymentRates[type].merchantFee}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            const updatedPricing = {
                              ...proposedPricing,
                              paymentRates: {
                                ...proposedPricing.paymentRates,
                                [type]: { ...proposedPricing.paymentRates[type], merchantFee: newValue }
                              }
                            };
                            setProposedPricing(updatedPricing);
                            
                            // Check if ALL merchant fees match original values
                            const allMatch = ['ACH', 'CC', 'AMEX', 'DEBIT'].every(pt => {
                              const currentFee = pt === type ? newValue : proposedPricing.paymentRates[pt]?.merchantFee || 0;
                              const originalFee = originalMerchantFees[pt] || 0;
                              return Math.abs(currentFee - originalFee) <= 0.01;
                            });
                            
                            // If all fees match original, re-enable auto mode
                            setMerchantFeesAutoCalculated(allMatch);
                          }}
                          onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                          className={`input-field text-sm w-24 pr-7 ${
                            !canEditMerchantFees 
                              ? 'bg-slate-100 cursor-not-allowed' 
                              : merchantFeesAutoCalculated 
                                ? 'bg-green-50' 
                                : 'bg-yellow-50'
                          }`}
                          step="0.01"
                          disabled={true}
                          title={
                            !canEditMerchantFees 
                              ? 'Merchant fees are locked and cannot be modified' 
                              : merchantFeesAutoCalculated 
                                ? 'Auto-calculated from service' 
                                : 'Manual input'
                          }
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-xs">
                          {type === 'ACH' && achFeeType === 'flat' ? '$ per trnx' : '%'}
                        </span>
                        {!canEditMerchantFees && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <Lock className="w-4 h-4 text-slate-400" title="Restricted" />
                          </div>
                        )}
                        {canEditMerchantFees && isFeeCustomized(type) && !merchantFeesAutoCalculated && (
                          <div className="absolute -top-2 -right-2">
                            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 border border-orange-300 rounded text-xs text-orange-700 shadow-sm">
                              <AlertCircle className="w-3 h-3" />
                              <span className="font-medium">Custom</span>
                            </div>
                          </div>
                        )}
                        {(merchantFeesAutoCalculated || (!merchantFeesAutoCalculated && canEditMerchantFees && doesCustomFeeMatchService(type))) && (
                          <span className="absolute -top-1 -right-1 flex h-3 w-3" title="Matches service manager rate">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
}
